import { makeNiceName } from 'utils/helpers';
import { PERMISSIONS } from 'constants/permissions';
import { ItemSkuSelect } from 'components/common/itemSkuSelect';
import { Col, Form, Input, InputRef, Row, Tag } from 'components';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { EditableCellProps } from './types';
import { EditableContext } from './context';

const { TextArea } = Input;
const DECIMAL_FIELDS = ['purchasePricePerPiece', 'discount', 'fees', 'weight', 'shippingCost', 'copper'];
const NON_DECIMAL_FIELDS = ['quantity', 'leadTimeMin', 'leadTimeMax'];
// const STRING_FIELDS = ['sku', 'description', 'attachment', 'leadTime', 'notes'];

const renderEditorByType = (
  inputType: string,
  dataIndex: string,
  inputRef: React.RefObject<any>,
  save: (...args: any[]) => void,
  hasPermission: boolean,
  onAddItemModalStateChange: () => void,
  record: any,
  itemSkuBrandId: string
) => {
  if (inputType === 'number') {
    return <Input ref={inputRef} onPressEnter={save} onBlur={save} type="number" />;
  }
  if (dataIndex === 'description') {
    return (
      <TextArea
        ref={inputRef as any}
        onBlur={save}
        autoSize={{ minRows: 2, maxRows: 4 }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            save();
          }
        }}
      />
    );
  }

  if (dataIndex === 'sku') {
    return (
      <ItemSkuSelect
        ref={inputRef as any}
        onChange={(_value, option: any) => {
          const updatedRow = {
            sku: option?.sku,
            description: option?.description,
            brandId: itemSkuBrandId,
            itemNumber: option?.id,
            uuid: option?.uuid,
          };
          save(updatedRow, 'sku');
        }}
        showAdd={hasPermission}
        onAdd={onAddItemModalStateChange}
        brandIds={itemSkuBrandId ? [itemSkuBrandId] : undefined}
        requireBrandId
      />
    );
  }

  if (dataIndex === 'leadTime') {
    return (
      <Row align={'middle'} gutter={2} wrap={false}>
        <Col>
          <Row wrap={false} gutter={2}>
            <Col>
              <Form.Item
                name="leadTimeMin"
                noStyle
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                      }

                      const num = Number(value);

                      if (!Number.isInteger(num)) {
                        return Promise.reject(new Error('Please enter a whole number without decimals.'));
                      }

                      if (num < 0) {
                        return Promise.reject(new Error('Value cannot be negative.'));
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder="Min"
                  onBlur={(e) => save(e.target.value, 'leadTimeMin')}
                  onPressEnter={(e) => {
                    const input = e.target as HTMLInputElement;
                    save(input.value, 'leadTimeMin');
                  }}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item
                name="leadTimeMax"
                noStyle
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                      }

                      const num = Number(value);

                      if (!Number.isInteger(num)) {
                        return Promise.reject(new Error('Please enter a whole number without decimals.'));
                      }

                      if (num < 0) {
                        return Promise.reject(new Error('Value cannot be negative.'));
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder="Max"
                  onBlur={(e) => save(e.target.value, 'leadTimeMax')}
                  onPressEnter={(e) => {
                    const input = e.target as HTMLInputElement;
                    save(input.value, 'leadTimeMax');
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
        <Col>
          <Row className="gap-4" justify="center">
            {['DAY', 'WEEK'].map((type) => (
              <Tag
                key={type}
                className={`tag ${record.leadTime === type && 'selected-tag'}`}
                onClick={() => {
                  save(type, 'leadTime');
                }}
              >
                {makeNiceName(type)}
              </Tag>
            ))}
          </Row>
        </Col>
      </Row>
    );
  }
  if (dataIndex === 'dimensions') {
    return (
      <Row gutter={8} wrap={false}>
        {['length', 'width', 'height'].map((key, idx) => (
          <Col key={key}>
            <Form.Item
              noStyle
              name={['dimensions', key]}
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);
                    if (isNaN(num)) {
                      return Promise.reject(new Error('Field must be a valid number.'));
                    }
                    if (num < 0) {
                      return Promise.reject(new Error('Field must be a positive number.'));
                    }
                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Field can have up to 2 decimal points.'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                type="number"
                placeholder={['L', 'W', 'H'][idx]}
                onPressEnter={(e) => {
                  e.preventDefault();
                  const input = e.target as HTMLInputElement;
                  save({ ...(record.dimensions || {}), [key]: Number(input.value || -1) }, 'dimensions');
                }}
                onBlur={(e) => {
                  const input = e.target as HTMLInputElement;
                  save({ ...(record.dimensions || {}), [key]: Number(input.value || -1) }, 'dimensions');
                }}
              />
            </Form.Item>
          </Col>
        ))}
      </Row>
    );
  }
  // Default fallback
  return <Input ref={inputRef} onPressEnter={save} onBlur={save} />;
};

export const EditableCell: React.FC<EditableCellProps> = ({
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  onAddItemModalStateChange,
  inputType = 'text',
  purchaseRequest,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const { hasAccess } = useAccessControl();
  const inputRef = useRef<InputRef>(null);
  const contextValue = useContext(EditableContext);
  const divRef = useRef<HTMLDivElement>(null);

  // Check if this cell should be in edit mode
  const shouldBeEditing =
    editable && record.key === contextValue.editingRow && dataIndex === contextValue.editingColumn;
  const toggleEdit = useCallback((value: boolean) => {
    setEditing(value);
  }, []);

  // When the cell should be in edit mode
  useEffect(() => {
    if (shouldBeEditing && !editing) {
      setEditing(true);
    }
  }, [shouldBeEditing, editing]);

  // When editing, focus and set value
  useEffect(() => {
    if (contextValue.form && dataIndex && record) {
      contextValue.form.setFieldsValue({
        [dataIndex]: record[dataIndex],
        ...(dataIndex === 'leadTime' && {
          leadTimeMin: record.leadTimeMin,
          leadTimeMax: record.leadTimeMax,
        }),
        ...(dataIndex === 'dimensions' && {
          dimensions: {
            length: record.dimensions?.length,
            width: record.dimensions?.width,
            height: record.dimensions?.height,
          },
        }),
      });
    }
    if (inputRef.current) {
      inputRef.current.focus();
    } else if (divRef.current) {
      divRef.current.focus();
    }
  }, [editing, dataIndex, record, contextValue.form]);
  const save = async (value?: any, type?: string) => {
    requestAnimationFrame(async () => {
      try {
        let payload = await contextValue?.form?.validateFields();
        if (value !== undefined && type) {
          if (type === 'sku') {
            payload = value;
          } else {
            payload = { [type]: value };
          }
        }
        if (payload) {
          toggleEdit(false);
          handleSave({ ...record, ...payload });
        }
      } catch (errInfo) {
        console.error('Save failed:', errInfo);
      }
    });
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <div
        ref={divRef}
        tabIndex={-1}
        onBlur={async (e) => {
          try {
            await contextValue?.form?.validateFields();
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              toggleEdit(false);
            }
          } catch (err) {
            console.warn(err);
          }
        }}
      >
        <Form.Item
          style={{ margin: 0 }}
          name={dataIndex !== 'dimensions' ? dataIndex : undefined}
          rules={[
            {
              required: dataIndex === 'sku',
              message: 'Required Field',
            },
            {
              validator: (_, value) => {
                if (value === undefined || value === null || value === '' || !NON_DECIMAL_FIELDS.includes(dataIndex)) {
                  return Promise.resolve();
                }

                const num = Number(value);

                if (!Number.isInteger(num)) {
                  return Promise.reject(new Error('Please enter a whole number without decimals.'));
                }

                if (num < 0) {
                  return Promise.reject(new Error('Value cannot be negative.'));
                }

                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (value === undefined || value === null || value === '' || !DECIMAL_FIELDS.includes(dataIndex)) {
                  return Promise.resolve();
                }
                const num = parseFloat(value);
                if (isNaN(num)) {
                  return Promise.reject(new Error('Field must be a valid number.'));
                }
                if (num < 0) {
                  return Promise.reject(new Error('Field must be a positive number.'));
                }
                if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                  return Promise.reject(new Error('Field can have up to 2 decimal points.'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {renderEditorByType(
            inputType,
            dataIndex,
            inputRef,
            save,
            hasAccess(PERMISSIONS.items.item.write),
            onAddItemModalStateChange,
            record,
            purchaseRequest?.brand?.id
          )}
        </Form.Item>
      </div>
    ) : (
      <div className="editable-cell-value-wrap" onClick={() => toggleEdit(true)}>
        <Form.Item
          noStyle
          name={dataIndex}
          rules={[
            {
              required: dataIndex === 'sku',
              message: 'Required Field',
            },
            {
              validator: (_, value) => {
                if (value === undefined || value === null || value === '' || !NON_DECIMAL_FIELDS.includes(dataIndex)) {
                  return Promise.resolve();
                }

                const num = Number(value);

                if (!Number.isInteger(num)) {
                  return Promise.reject(new Error('Please enter a whole number without decimals.'));
                }

                if (num < 0) {
                  return Promise.reject(new Error('Value cannot be negative.'));
                }

                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (value === undefined || value === null || value === '' || !DECIMAL_FIELDS.includes(dataIndex)) {
                  return Promise.resolve();
                }
                const num = parseFloat(value);
                if (isNaN(num)) {
                  return Promise.reject(new Error('Field must be a valid number.'));
                }
                if (num < 0) {
                  return Promise.reject(new Error('Field must be a positive number.'));
                }
                if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                  return Promise.reject(new Error('Field can have up to 2 decimal points.'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {children}
        </Form.Item>
      </div>
    );
  }

  return <td {...(restProps as any)}>{childNode}</td>;
};
